/**
 * FeatureFlagRoute — a route behind a runtime flag.
 *
 * The flag comes from the registry in `@tonnie/foundation`, resolved for this
 * caller by the API (`useFeatureFlags`). While it is off — or not known,
 * because the flags could not be read — the URL does not exist: the same 404
 * page as any unknown route, as the API answers 404 for the feature's
 * endpoints. Nothing of the feature mounts, so nothing of it is fetched.
 */
import type { FeatureFlagKey } from '@tonnie/foundation';
import { useFeatureFlags } from '@tonnie/sdk';
import type { ReactNode } from 'react';

import { LoadingSpinner } from '@/components/shell/LoadingSpinner';
import { NotFoundPage } from '@/core/pages/NotFoundPage';

export function FeatureFlagRoute({
  flag,
  children,
}: {
  flag: FeatureFlagKey;
  children: ReactNode;
}) {
  const { flags, isResolved, isLoading } = useFeatureFlags();
  if (isLoading) return <LoadingSpinner />;
  return isResolved && flags[flag] ? <>{children}</> : <NotFoundPage />;
}
